import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import UndoIcon from '@material-ui/icons/Undo';
import { useSiteTakeoff } from '../SiteTakeoffContext';
import ModeSwitcher from './ModeSwitcher';
import TakeoffSelector from './TakeoffSelector';
import AreasMode from './modes/AreasMode';
import TakeoffMode from './modes/TakeoffMode';
import QueueMode from './modes/QueueMode';
import ProjectsMode from './modes/ProjectsMode';
import AreaEditor from '../editor/AreaEditor';

const useStyles = makeStyles(() => ({
  sidebar: {
    width: 360,
    minWidth: 360,
    background: '#ffffff',
    borderLeft: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  switcherWrap: {
    padding: '16px 16px 0 16px',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: 16,
  },
  saveBar: {
    padding: '12px 16px',
    borderTop: '1px solid #e0e0e0',
    background: '#fafafa',
  },
  saveBtn: {
    textTransform: 'none',
    fontWeight: 500,
  },
  saveActions: {
    display: 'flex',
    gap: 8,
  },
}));

const Sidebar = () => {
  const classes = useStyles();
  const ctx = useSiteTakeoff();
  const {
    url, activeMode, setActiveMode,
    editingAreaId, setEditingAreaId,
    editingShapeAreaId, startEditShape, stopEditShape,
    draggableAreaId, setDraggableAreaId,
    areas, jobsite,
    takeoffs, takeoff, saving, dirty, confirmDirtyAction,
    updateArea, deleteArea, duplicateArea, replaceAreaWithAreas,
    ungroupAll,
    saveTakeoff,
    newTakeoff, switchTakeoff, deleteTakeoff, renameTakeoff,
    startDraw, snapping, setSnapping,
    handleDrawExclusion,
    legacyAreas, hasLegacyAreas, importLegacyAreas, legacyImportCandidates,
    ignoreLegacyAreas, restoreIgnoredLegacyAreas,
    zoomToArea, overlayManager,
    setBulkEditOpen,
    hiddenAreaIds, toggleAreaVisibility,
    placementQueue, updatePlacementQueue,
    placingItemId, setPlacingItemId, placementPaused,
    canUndo, undoLastChange,
  } = ctx;

  const editingArea = editingAreaId
    ? areas.find(a => (a._id || a.id) === editingAreaId)
    : null;

  const handleEditAreaClose = () => {
    stopEditShape();
    setEditingAreaId(null);
    setDraggableAreaId(null);
  };

  const renderSaveBar = () => {
    if (!jobsite?._id) return null;
    const label = saving ? 'Saving…' : dirty ? 'Save Takeoff' : 'All saved';
    return (
      <div className={classes.saveBar}>
        <div className={classes.saveActions}>
          <Button
            variant="outlined"
            size="small"
            disabled={!canUndo || saving}
            onClick={undoLastChange}
            startIcon={<UndoIcon />}
          >
            Undo
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="small"
            className={classes.saveBtn}
            data-tour="site-takeoff-save-button"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => saveTakeoff(takeoff?.name)}
            disabled={saving || !dirty}
          >
            {label}
          </Button>
        </div>
      </div>
    );
  };

  if (editingArea) {
    const isEditingShape = editingShapeAreaId === editingAreaId;
    return (
      <div className={classes.sidebar}>
        <AreaEditor
          area={editingArea}
          areas={areas}
          onClose={handleEditAreaClose}
          onUpdate={(updates) => updateArea(editingAreaId, updates)}
          onDelete={() => { deleteArea(editingAreaId); handleEditAreaClose(); }}
          onDuplicate={(groupOptions) => {
            stopEditShape();
            const newId = duplicateArea(editingAreaId, groupOptions);
            if (newId) {
              setTimeout(() => {
                setEditingAreaId(newId);
                setDraggableAreaId(newId);
              }, 50);
            }
          }}
          isEditingShape={isEditingShape}
          onStartEditShape={() => {
            setDraggableAreaId(null);
            startEditShape(editingAreaId);
          }}
          onStopEditShape={stopEditShape}
          onDrawExclusion={handleDrawExclusion}
          onEditArea={setEditingAreaId}
          onSplitArea={(replacementAreas) => {
            const newIds = replaceAreaWithAreas(editingAreaId, replacementAreas, 'Split area');
            setEditingAreaId(newIds[0] || null);
          }}
          isDragging={draggableAreaId === editingAreaId}
          onStopDrag={() => setDraggableAreaId(null)}
        />
        {renderSaveBar()}
      </div>
    );
  }

  return (
    <div className={classes.sidebar}>
      {jobsite?._id && (
        <div data-tour="site-takeoff-selector">
          <TakeoffSelector
            takeoffs={takeoffs}
            activeTakeoffId={takeoff?._id}
            activeTakeoffName={takeoff?.name || ''}
            onSwitchTakeoff={(id) => confirmDirtyAction(() => { switchTakeoff(id); setEditingAreaId(null); })}
            onNewTakeoff={() => confirmDirtyAction(() => { newTakeoff(); setEditingAreaId(null); })}
            onDeleteTakeoff={(id) => confirmDirtyAction(() => { deleteTakeoff(id); setEditingAreaId(null); })}
            onRenameTakeoff={renameTakeoff}
            disabled={saving}
          />
        </div>
      )}

      <div className={classes.switcherWrap} data-tour="site-takeoff-mode-switcher">
        <ModeSwitcher
          activeMode={activeMode}
          onModeChange={setActiveMode}
          queueCount={(placementQueue || []).filter(q => q.status === 'unplaced').length}
        />
      </div>

      <div className={classes.content}>
        {activeMode === 'areas' && (
          <AreasMode
            jobsite={jobsite}
            areas={areas}
            onDrawArea={(tool) => startDraw(tool)}
            snapping={snapping}
            onToggleSnapping={() => setSnapping(v => !v)}
            onEditArea={setEditingAreaId}
            onZoomToArea={zoomToArea}
            hasLegacyAreas={hasLegacyAreas}
            legacyAreaCount={(legacyImportCandidates || []).length}
            legacyImportCandidates={legacyImportCandidates}
            onImportLegacyAreas={async (selectedAreas, name) => {
              await importLegacyAreas(selectedAreas?.length ? selectedAreas : legacyAreas, name || 'Imported Areas');
              setEditingAreaId(null);
            }}
            onIgnoreLegacyAreas={ignoreLegacyAreas}
            ignoredLegacyAreaCount={ctx.ignoredLegacyAreaIds?.length || 0}
            onRestoreIgnoredLegacyAreas={restoreIgnoredLegacyAreas}
            saving={saving}
            onBulkEdit={() => setBulkEditOpen(true)}
            hiddenAreaIds={hiddenAreaIds}
            onToggleVisibility={toggleAreaVisibility}
            onUngroupAll={ungroupAll}
          />
        )}
        {activeMode === 'takeoff' && (
          <TakeoffMode
            areas={areas}
            rateOverrides={takeoff?.productionRates || {}}
          />
        )}
        {activeMode === 'queue' && (
          <QueueMode
            queue={placementQueue}
            onQueueChange={updatePlacementQueue}
            placingItemId={placingItemId}
            onStartPlacing={setPlacingItemId}
            placementPaused={placementPaused}
          />
        )}
        {activeMode === 'projects' && (
          <ProjectsMode
            url={url}
            jobsite={jobsite}
            overlays={overlayManager?.overlays || []}
            activeOverlayId={overlayManager?.activeOverlayId}
            uploading={overlayManager?.uploading}
            savedOverlayUrls={overlayManager?.savedOverlayUrls || []}
            onUploadFile={overlayManager?.uploadOverlay}
            onAddExistingOverlay={overlayManager?.addExistingOverlay}
            onSetVisible={overlayManager?.setOverlayVisible}
            onSetOpacity={overlayManager?.setOverlayOpacity}
            onSetMasterOpacity={overlayManager?.setMasterOpacity}
            onSetLocked={overlayManager?.setOverlayLocked}
            onSetCategory={overlayManager?.setOverlayCategory}
            onActivate={overlayManager?.activateOverlay}
            onRemove={overlayManager?.removeOverlay}
          />
        )}
      </div>

      {renderSaveBar()}
    </div>
  );
};

export default Sidebar;
